/**
 * Video Banner Block Frontend JavaScript
 *
 * @package Modern_Blocks
 */
import './frontend.css';

(function() {
	'use strict';

	// Simple mobile detection based on screen size only
	function isMobile() {
		return window.innerWidth < 768;
	}
	

	// Initialize video banner when DOM is ready
	document.addEventListener('DOMContentLoaded', function() {
		initVideoBanners();
	});

	/**
	 * Initialize all video banner blocks
	 */
	function initVideoBanners() {
		const videoBanners = document.querySelectorAll('.video-banner-container');
		
		videoBanners.forEach(banner => {
			const video = banner.querySelector('video');
			
			// If mobile (screen width < 768px), don't load video
			if (isMobile() && video) {
				console.log('Mobile screen detected - skipping video load');
				banner.classList.add('mobile-optimized');
				
				// Ensure video is completely disabled on mobile
				video.src = '';
				video.load();
				video.style.display = 'none';
				video._mobileBlocked = true;
				
				console.log('Video element disabled for mobile:', video);
			} else if (video) {
				// Desktop - load video dynamically
				loadVideoDynamically(video, banner);
			}
			
			initVideoBanner(banner);
		});
	}

	/**
	 * Initialize individual video banner
	 */
	function initVideoBanner(banner) {
		const content = banner.querySelector('.video-banner-content');
		
		if (!content) return;

		// Set up intersection observer for animations
		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					animateContent(entry.target);
					observer.unobserve(entry.target);
				}
			});
		}, {
			threshold: 0.1,
			rootMargin: '0px 0px -50px 0px'
		});

		observer.observe(content);
	}

	/**
	 * Dynamically load video for desktop devices only
	 */
	function loadVideoDynamically(video, banner) {
		const videoSrc = video.dataset.videoSrc;
		if (!videoSrc) return;

		// Double-check mobile state
		if (isMobile() || video._mobileBlocked) {
			console.log('Video loading blocked - mobile detected or video marked as blocked');
			return;
		}

		console.log('Loading video for desktop:', videoSrc);
		
		// Set video source
		video.src = videoSrc;

		// Set video attributes from data attributes
		video.autoplay = video.dataset.autoplay === 'true';
		video.muted = video.dataset.muted === 'true';
		video.loop = video.dataset.loop === 'true';
		video.controls = video.dataset.controls === 'true';
		
		// Set poster if available
		if (video.dataset.poster) {
			video.poster = video.dataset.poster;
		}

		// Set preload based on connection speed
		if (navigator.connection) {
			const connection = navigator.connection;
			if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
				video.preload = 'none';
			} else if (connection.effectiveType === '3g') {
				video.preload = 'metadata';
			} else {
				video.preload = 'auto';
			}
		} else {
			video.preload = 'metadata';
		}

		// Setup video events after loading
		setupVideoEvents(video, banner);

		// Add loading class
		banner.classList.add('video-loading');

		// Handle video loading events
		video.addEventListener('loadstart', function() {
			banner.classList.add('video-loading');
		});

		video.addEventListener('canplay', function() {
			banner.classList.remove('video-loading');
			banner.classList.add('video-loaded');
		});

		video.addEventListener('error', function() {
			banner.classList.remove('video-loading');
			banner.classList.add('video-error');
			console.error('Video failed to load:', videoSrc);
		});
	}

	/**
	 * Setup video event handlers
	 */
	function setupVideoEvents(video, banner) {
		// Ensure video plays when in viewport
		const videoObserver = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					// Video is in viewport, ensure it's playing
					if (video.paused && video.autoplay) {
						video.play().catch(e => {
							console.log('Video autoplay failed:', e);
						});
					}
				} else {
					// Video is out of viewport, pause it
					if (!video.paused) {
						video.pause();
					}
				}
			});
		}, {
			threshold: 0.5
		});

		videoObserver.observe(video);

		// Handle video loading
		video.addEventListener('loadeddata', function() {
			banner.classList.add('video-loaded');
		});

		// Handle video errors
		video.addEventListener('error', function() {
			banner.classList.add('video-error');
			console.error('Video failed to load:', video.src);
		});

		// Handle video play/pause
		video.addEventListener('play', function() {
			banner.classList.add('video-playing');
		});

		video.addEventListener('pause', function() {
			banner.classList.remove('video-playing');
		});

		// Handle video ended
		video.addEventListener('ended', function() {
			if (video.loop) {
				video.currentTime = 0;
				video.play().catch(e => {
					console.log('Video replay failed:', e);
				});
			}
		});
	}

	/**
	 * Animate content with specified animation type
	 */
	function animateContent(content) {
		const animationType = content.dataset.animationType || 'fade-up';
		const delay = parseFloat(content.dataset.animationDelay) || 0.2;

		// Set initial animation state
		setInitialAnimationState(content, animationType);

		// Apply animation after delay
		setTimeout(() => {
			content.classList.add('animate-in');
		}, delay * 1000);
	}

	/**
	 * Set initial animation state based on type
	 */
	function setInitialAnimationState(element, animationType) {
		// Remove any existing animation classes
		element.classList.remove('animate-in', 'animate-fade-up', 'animate-fade-in', 'animate-slide-left', 'animate-slide-right', 'animate-scale-up', 'animate-bounce-in');
		
		// Add the appropriate animation class
		element.classList.add(`animate-${animationType}`);
	}

	/**
	 * Handle responsive changes (orientation, window resize)
	 */
	function handleResponsiveChanges() {
		const debouncedHandler = debounce(() => {
			const videoBanners = document.querySelectorAll('.video-banner-container');
			
			videoBanners.forEach(banner => {
				const video = banner.querySelector('video');
				if (!video) return;

				if (isMobile()) {
					// Mobile: ensure video is not loaded
					if (video.src) {
						video.src = '';
						video.load();
					}
					video.style.display = 'none';
					video._mobileBlocked = true;
					banner.classList.add('mobile-optimized');
					banner.classList.remove('video-loaded', 'video-loading');
				} else {
					// Desktop: load video if not already loaded
					video.style.display = '';
					video._mobileBlocked = false;
					if (!video.src && video.dataset.videoSrc) {
						loadVideoDynamically(video, banner);
					}
					banner.classList.remove('mobile-optimized');
				}
			});
		}, 250);
		
		// Handle window resize
		window.addEventListener('resize', debouncedHandler);
		
		// Handle orientation change (mobile devices)
		window.addEventListener('orientationchange', () => {
			// Small delay to allow orientation change to complete
			setTimeout(debouncedHandler, 100);
		});
	}



	/**
	 * Debounce function for performance
	 */
	function debounce(func, wait) {
		let timeout;
		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	}

	/**
	 * Initialize all functionality
	 */
	function init() {
		initVideoBanners();
		handleResponsiveChanges();
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	// Re-initialize for dynamically loaded content
	if (typeof wp !== 'undefined' && wp.hooks) {
		wp.hooks.addAction('blocks/video-banner/loaded', 'modern-blocks', init);
	}

})();
